import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  FormControlLabel,
  Checkbox,
  IconButton,
  Box,
  Divider,
  MenuItem,
  FormGroup,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  CardActions,
  Stepper,
  Step,
  StepLabel,
  Fab,
  Collapse,
  Stack
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  School as SchoolIcon,
  Church as ChurchIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandIcon
} from '@mui/icons-material';
import axios from '../utils/axios';
import { useError } from '../contexts/ErrorContext';
import { useLoading } from '../contexts/LoadingContext';

const ReligiousEducationForm = ({ open, onClose, onSuccess, schoolYear, currentSchoolYear }) => {
  // The school year this manual registration will be filed under. Falls back to
  // the current year when the parent didn't supply one (older callsites / safety).
  const targetSchoolYear = schoolYear || currentSchoolYear || '';
  const isPastYear = Boolean(targetSchoolYear && currentSchoolYear && targetSchoolYear !== currentSchoolYear);
  // Relationship options for parents/guardians
  const relationshipOptions = [
    'Father', 'Mother', 'Grandparent', 'Guardian', 'Other'
  ];

  const [formData, setFormData] = useState({
    family: {
      name: '',
      parents: [
        { name: '', relationship: '', maidenName: '' }
      ],
      primaryContact: {
        name: '',
        email: '',
        phone: ''
      }
    },
    children: [{
      name: '',
      dateOfBirth: null,
      grade: 1,
      catholicSchool: {
        attends: false,
        schoolName: ''
      },
      sacraments: {
        received: [],
        preparing: []
      },
      baptism: {
        baptizedAtStBridget: false,
        previouslyEnrolled: false
      },
      program: []
    }],
    permissions: {
      photoPermission: false
    },
    notifications: {
      sendEmails: false
    },
    payment: {
      amount: 0,
      totalPaid: 0,
      balance: 0,
      isHardship: false,
      transactions: []
    },
    signature: {
      name: '',
      date: new Date().toISOString().split('T')[0]
    }
  });

  const [settings, setSettings] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [expandedChildren, setExpandedChildren] = useState({ 0: true }); // First child expanded by default
  const [newPayment, setNewPayment] = useState({
    amount: '',
    method: 'cash',
    notes: ''
  });
  
  const { showError } = useError();
  const { showLoading, hideLoading } = useLoading();
  const childrenContainerRef = useRef(null);

  // Derive program select options from the live program list (mirrors public form)
  const programValueFor = (program) => program.name.toLowerCase().replace(/\s+/g, '-');
  const programOptions = programs.map(program => ({
    value: programValueFor(program),
    label: `${program.name}${program.description ? ': ' + program.description : ''}`
  }));

  const sacramentOptions = [
    { value: 'baptism', label: 'Baptism' },
    { value: 'communion', label: 'First Communion' },
    { value: 'confirmation', label: 'Confirmation' }
  ];

  // Fetch settings + programs for fee calculation and program selection.
  // Refetch programs when the target school year changes so past-year registrations
  // get the program list that was active for that year.
  useEffect(() => {
    if (open) {
      fetchSettings();
      fetchPrograms();
      // Reset expanded state when opening
      setExpandedChildren({ 0: true });
    }
  }, [open, targetSchoolYear]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/religious-education/settings/status-public');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      showError('Failed to load settings. Some features may not work properly.');
    }
  };

  const fetchPrograms = async () => {
    try {
      // For the current school year (or unknown year), use the public endpoint that
      // already filters to active programs for the current year. For past years, hit
      // the authenticated list endpoint with an explicit year filter — that endpoint
      // returns an array directly rather than { programs: [...] }.
      if (!isPastYear) {
        const response = await axios.get('/programs/current');
        setPrograms(response.data.programs || []);
      } else {
        const response = await axios.get('/programs', {
          params: { schoolYear: targetSchoolYear, active: 'true' }
        });
        setPrograms(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Failed to fetch programs:', error);
      setPrograms([]);
    }
  };

  // Calculate detailed fee breakdown with itemization
  const calculateDetailedBreakdown = () => {
    if (!settings) return { items: [], total: 0 };

    const items = [];
    let total = 0;
    const now = new Date();
    const isDiscountActive = settings.discount?.enabled && 
                             settings.discount?.endDate && 
                             now < new Date(settings.discount.endDate);

    formData.children.forEach((child, index) => {
      const isCatholicSchoolStudent = child.catholicSchool.attends;
      const childName = child.name || `Child ${index + 1}`;
      
      // Registration fee (free for Catholic school students)
      if (!isCatholicSchoolStudent) {
        if (index === 0) {
          // First child registration
          const fee = isDiscountActive ? 
            (settings.discount.discountedRegistrationFee || 0) : 
            (settings.registrationFee || 0);
          
          if (fee > 0) {
            items.push({
              type: 'registration',
              description: `Registration Fee - ${childName} (1st child)`,
              originalPrice: settings.registrationFee || 0,
              discountedPrice: isDiscountActive ? (settings.discount.discountedRegistrationFee || 0) : null,
              amount: fee,
              isDiscount: isDiscountActive
            });
            total += fee;
          }
        } else {
          // Additional child registration
          const fee = isDiscountActive ? 
            (settings.discount.discountedAdditionalChildFee || 0) : 
            (settings.additionalChildFee || 0);
          
          if (fee > 0) {
            items.push({
              type: 'registration',
              description: `Registration Fee - ${childName} (additional child)`,
              originalPrice: settings.additionalChildFee || 0,
              discountedPrice: isDiscountActive ? (settings.discount.discountedAdditionalChildFee || 0) : null,
              amount: fee,
              isDiscount: isDiscountActive
            });
            total += fee;
          }
        }
      } else {
        // Catholic school student - free registration
        items.push({
          type: 'registration',
          description: `Registration Fee - ${childName} (Catholic School Student)`,
          originalPrice: index === 0 ? (settings.registrationFee || 0) : (settings.additionalChildFee || 0),
          amount: 0,
          isFree: true
        });
      }

      // Sacramental preparation fees
      if (child.sacraments.preparing.length > 0) {
        const sacramentFee = settings.sacramentalPreparationFee || 0;
        const sacramentsText = child.sacraments.preparing.map(s => 
          s.charAt(0).toUpperCase() + s.slice(1)
        ).join(', ');
        
        items.push({
          type: 'sacrament',
          description: `Sacramental Preparation - ${childName} (${sacramentsText})`,
          amount: sacramentFee,
          originalPrice: sacramentFee
        });
        total += sacramentFee;
      }
    });

    return { items, total };
  };

  // Calculate balance based on total due and payments made
  const updatePaymentBalance = () => {
    const breakdown = calculateDetailedBreakdown();
    const totalPaid = formData.payment.transactions.reduce((sum, transaction) => sum + transaction.amount, 0);
    const balance = breakdown.total - totalPaid;

    setFormData(prev => ({
      ...prev,
      payment: {
        ...prev.payment,
        amount: breakdown.total,
        totalPaid: totalPaid,
        balance: Math.max(0, balance)
      }
    }));
  };

  // Update payment calculations when form changes
  useEffect(() => {
    if (settings) {
      updatePaymentBalance();
    }
  }, [formData.children, formData.payment.transactions, settings]);

  const handleChange = (path, value) => {
    const keys = path.split('.');
    const newData = { ...formData };
    let current = newData;
    
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    setFormData(newData);
  };

  const handleChildChange = (index, field, value) => {
    const newChildren = [...formData.children];
    
    if (field.includes('.')) {
      // Handle nested field like 'catholicSchool.attends'
      const [parentField, childField] = field.split('.');
      newChildren[index] = {
        ...newChildren[index],
        [parentField]: {
          ...newChildren[index][parentField],
          [childField]: value
        }
      };
    } else {
      newChildren[index] = {
        ...newChildren[index],
        [field]: value
      };
    }
    
    setFormData({
      ...formData,
      children: newChildren
    });
  };

  const handleMultiSelectChange = (index, field, value) => {
    const newChildren = [...formData.children];
    const newValue = typeof value === 'string' ? value.split(',') : value;
    
    newChildren[index] = {
      ...newChildren[index],
      sacraments: {
        ...newChildren[index].sacraments,
        [field]: newValue
      }
    };

    // If adding to "received", remove any matching items from "preparing"
    if (field === 'received') {
      const preparing = newChildren[index].sacraments.preparing || [];
      newChildren[index].sacraments.preparing = preparing.filter(prep => !newValue.includes(prep));
    }
    
    setFormData({
      ...formData,
      children: newChildren
    });
  };

  const addChild = () => {
    const newChildIndex = formData.children.length;
    setFormData({
      ...formData,
      children: [
        ...formData.children,
        {
          name: '',
          dateOfBirth: null,
          grade: 1,
          catholicSchool: {
            attends: false,
            schoolName: ''
          },
          sacraments: {
            received: [],
            preparing: []
          },
          baptism: {
            baptizedAtStBridget: false,
            previouslyEnrolled: false
          },
          program: []
        }
      ]
    });

    // Expand the new child and scroll to it
    setExpandedChildren(prev => ({
      ...prev,
      [newChildIndex]: true
    }));

    // Auto-scroll to the new child section
    setTimeout(() => {
      if (childrenContainerRef.current) {
        const childElements = childrenContainerRef.current.querySelectorAll('[data-child-index]');
        const newChildElement = childElements[newChildIndex];
        if (newChildElement) {
          newChildElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 100);
  };

  const removeChild = (index) => {
    if (formData.children.length > 1) {
      const newChildren = formData.children.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        children: newChildren
      });

      // Update expanded state
      const newExpanded = {};
      Object.keys(expandedChildren).forEach(key => {
        const keyIndex = parseInt(key);
        if (keyIndex < index) {
          newExpanded[keyIndex] = expandedChildren[key];
        } else if (keyIndex > index) {
          newExpanded[keyIndex - 1] = expandedChildren[key];
        }
      });
      setExpandedChildren(newExpanded);
    }
  };

  const toggleChildExpanded = (index) => {
    setExpandedChildren(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const addPayment = () => {
    if (!newPayment.amount || parseFloat(newPayment.amount) <= 0) {
      showError('Please enter a valid payment amount');
      return;
    }

    const payment = {
      amount: parseFloat(newPayment.amount),
      method: newPayment.method,
      notes: newPayment.notes,
      date: new Date().toISOString()
    };

    setFormData(prev => ({
      ...prev,
      payment: {
        ...prev.payment,
        transactions: [...prev.payment.transactions, payment]
      }
    }));

    // Reset new payment form
    setNewPayment({
      amount: '',
      method: 'cash',
      notes: ''
    });
  };

  const removePayment = (index) => {
    setFormData(prev => ({
      ...prev,
      payment: {
        ...prev.payment,
        transactions: prev.payment.transactions.filter((_, i) => i !== index)
      }
    }));
  };

  const handleSubmit = async () => {
    try {
      showLoading();

      // Format data to match backend expectations
      const submitData = {
        ...formData,
        family: {
          ...formData.family,
          name: generateFamilyName(), // Auto-generate from parent names
          parents: formData.family.parents.filter(p => p.name && p.relationship)
        },
        source: 'manual', // Mark this as a manual registration
        ...(targetSchoolYear ? { schoolYear: targetSchoolYear } : {}),
        // Fix timezone issue: convert date strings to proper dates in local timezone
        children: formData.children.map(child => ({
          ...child,
          program: child.program.length > 0
            ? child.program
            : (programs.length === 1 ? [programValueFor(programs[0])] : []),
          dateOfBirth: child.dateOfBirth ? (() => {
            // Parse date string as local date to prevent timezone shifting
            const [year, month, day] = child.dateOfBirth.split('-');
            const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            return localDate.toISOString();
          })() : child.dateOfBirth
        })),
        payment: {
          ...formData.payment,
          status: formData.payment.balance <= 0 ? 'paid' :
                 formData.payment.isHardship ? 'hardship' : 'open',
          method: formData.payment.transactions.length > 0 ?
                 formData.payment.transactions[formData.payment.transactions.length - 1].method : 'cash'
        }
      };

      await axios.post('/religious-education', submitData);
      onSuccess();
      onClose();
      
      // Reset form for next use
      resetForm();
    } catch (error) {
      showError('Failed to create religious education registration');
    } finally {
      hideLoading();
    }
  };

  const resetForm = () => {
    setFormData({
      family: {
        name: '',
        parents: [{ name: '', relationship: '', maidenName: '' }],
        primaryContact: { name: '', email: '', phone: '' }
      },
      children: [{
        name: '', dateOfBirth: null, grade: 1,
        catholicSchool: { attends: false, schoolName: '' },
        sacraments: { received: [], preparing: [] },
        baptism: { baptizedAtStBridget: false, previouslyEnrolled: false },
        program: []
      }],
      permissions: { photoPermission: false },
      notifications: { sendEmails: false },
      payment: { amount: 0, totalPaid: 0, balance: 0, isHardship: false, transactions: [] },
      signature: { name: '', date: new Date().toISOString().split('T')[0] }
    });
    setExpandedChildren({ 0: true });
    setNewPayment({ amount: '', method: 'cash', notes: '' });
  };

  // Generate family name from parent names
  const generateFamilyName = () => {
    const parentNames = (formData.family.parents || [])
      .filter(p => p.name)
      .map(p => {
        const nameParts = p.name.trim().split(' ');
        return nameParts[nameParts.length - 1]; // Get last name
      });

    if (parentNames.length === 0) return '';
    if (parentNames.length === 1) return `${parentNames[0]} Family`;

    const uniqueNames = [...new Set(parentNames)];
    if (uniqueNames.length === 1) return `${uniqueNames[0]} Family`;
    return `${uniqueNames.join(' / ')} Family`;
  };

  const isFormValid = () => {
    // Check basic required fields - now checking for at least one parent with name and relationship
    const hasValidParent = formData.family.parents &&
                          formData.family.parents.length > 0 &&
                          formData.family.parents[0].name &&
                          formData.family.parents[0].relationship;

    const hasBasicInfo = hasValidParent &&
                        formData.family.primaryContact.name &&
                        formData.family.primaryContact.email &&
                        formData.signature.name;

    // Check if we have at least one child
    const hasChildren = formData.children.length > 0;

    // Check if all children have required info — program selection is only required
    // when more than one program is available (auto-filled at submit when exactly one).
    const programSelectionRequired = programs.length > 1;
    const childrenValid = formData.children.every(child =>
      child.name &&
      child.dateOfBirth && String(child.dateOfBirth).trim() !== '' &&
      (!programSelectionRequired || child.program.length > 0)
    );

    const isValid = hasBasicInfo && hasChildren && childrenValid;

    // Debug logging to help identify issues
    if (!isValid) {
      console.log('Form validation failed:', {
        hasBasicInfo,
        hasValidParent,
        hasChildren,
        childrenValid,
        parents: formData.family.parents,
        contactName: !!formData.family.primaryContact.name,
        contactEmail: !!formData.family.primaryContact.email,
        signatureName: !!formData.signature.name,
        children: formData.children.map((child, i) => ({
          index: i,
          hasName: !!child.name,
          hasDOB: !!child.dateOfBirth,
          hasProgram: child.program.length > 0,
          programs: child.program
        }))
      });
    }

    return isValid;
  };

  const getValidationStatus = () => {
    const missingItems = [];

    const hasValidParent = formData.family.parents &&
                          formData.family.parents.length > 0 &&
                          formData.family.parents[0].name &&
                          formData.family.parents[0].relationship;
    if (!hasValidParent) missingItems.push('Parent/Guardian Info');
    if (!formData.family.primaryContact.name) missingItems.push('Primary Contact Name');
    if (!formData.family.primaryContact.email) missingItems.push('Primary Contact Email');
    if (!formData.signature.name) missingItems.push('Signature Name');

    if (formData.children.length === 0) {
      missingItems.push('At least one child');
    } else {
      formData.children.forEach((child, index) => {
        if (!child.name) missingItems.push(`Child ${index + 1} Name`);
        if (!child.dateOfBirth || String(child.dateOfBirth).trim() === '') missingItems.push(`Child ${index + 1} Date of Birth`);
        if (programs.length > 1 && child.program.length === 0) missingItems.push(`Child ${index + 1} Program Selection`);
      });
    }

    return missingItems;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6">New Manual Registration</Typography>
          {targetSchoolYear && (
            <Chip
              label={`${targetSchoolYear} School Year`}
              color={isPastYear ? 'warning' : 'primary'}
              size="small"
              variant={isPastYear ? 'filled' : 'outlined'}
            />
          )}
          {isPastYear && (
            <Chip label="Past Year" color="warning" size="small" />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {isPastYear && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are registering for the <strong>{targetSchoolYear}</strong> school year, not the current year
            ({currentSchoolYear}). Programs shown below are the ones that were active for {targetSchoolYear}.
          </Alert>
        )}
        <Alert severity="info" sx={{ mb: 3 }}>
          This form is for manual registrations made in-person at the parish office.
          All required information should be collected from the family.
        </Alert>

        <Grid container spacing={3}>
          {/* Family Information */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon color="primary" />
                  Family Information
                </Typography>

                {/* Parents/Guardians */}
                <Typography variant="subtitle1" fontWeight="medium" sx={{ mt: 2, mb: 1 }}>
                  Parents / Guardians
                </Typography>

                {formData.family.parents.map((parent, index) => (
                  <Box key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {index === 0 ? 'Parent/Guardian 1' : 'Parent/Guardian 2'}
                      </Typography>
                      {index > 0 && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            const newParents = formData.family.parents.filter((_, i) => i !== index);
                            handleChange('family.parents', newParents);
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Full Name"
                          value={parent.name}
                          onChange={(e) => {
                            const newParents = [...formData.family.parents];
                            newParents[index] = { ...newParents[index], name: e.target.value };
                            handleChange('family.parents', newParents);
                          }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small" required>
                          <InputLabel>Relationship</InputLabel>
                          <Select
                            value={parent.relationship}
                            label="Relationship"
                            onChange={(e) => {
                              const newParents = [...formData.family.parents];
                              newParents[index] = { ...newParents[index], relationship: e.target.value };
                              handleChange('family.parents', newParents);
                            }}
                          >
                            {relationshipOptions.map(rel => (
                              <MenuItem key={rel} value={rel}>{rel}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Maiden Name"
                          value={parent.maidenName || ''}
                          onChange={(e) => {
                            const newParents = [...formData.family.parents];
                            newParents[index] = { ...newParents[index], maidenName: e.target.value };
                            handleChange('family.parents', newParents);
                          }}
                          helperText="Optional"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}

                {formData.family.parents.length < 2 && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      handleChange('family.parents', [
                        ...formData.family.parents,
                        { name: '', relationship: '', maidenName: '' }
                      ]);
                    }}
                    sx={{ mb: 2 }}
                  >
                    + Add Second Parent/Guardian
                  </Button>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Primary Contact */}
                <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                  Primary Contact for Religious Education
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Contact Name"
                      value={formData.family.primaryContact.name}
                      onChange={(e) => handleChange('family.primaryContact.name', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={formData.family.primaryContact.email}
                      onChange={(e) => handleChange('family.primaryContact.email', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={formData.family.primaryContact.phone}
                      onChange={(e) => handleChange('family.primaryContact.phone', e.target.value)}
                      required
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Children Information */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SchoolIcon color="primary" />
                    Children Information
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addChild}
                    variant="outlined"
                    size="small"
                  >
                    Add Child
                  </Button>
                </Box>
                
                <Box ref={childrenContainerRef}>
                  {formData.children.map((child, index) => (
                    <Card 
                      key={index}
                      data-child-index={index}
                      variant="outlined" 
                      sx={{ mb: 2, border: '1px solid', borderColor: 'primary.main', borderRadius: 2 }}
                    >
                      <CardActions sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText', py: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {child.name || `Child ${index + 1}`}
                          </Typography>
                          <Box>
                            <IconButton 
                              onClick={() => toggleChildExpanded(index)} 
                              sx={{ color: 'inherit' }}
                              size="small"
                            >
                              {expandedChildren[index] ? <ExpandLessIcon /> : <ExpandIcon />}
                            </IconButton>
                            {formData.children.length > 1 && (
                              <IconButton 
                                onClick={() => removeChild(index)} 
                                sx={{ color: 'inherit' }}
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      </CardActions>
                      
                      <Collapse in={expandedChildren[index]}>
                        <CardContent>
                          <Grid container spacing={2}>
                            {/* Basic Info */}
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Child's Name"
                                value={child.name}
                                onChange={(e) => handleChildChange(index, 'name', e.target.value)}
                                required
                              />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <TextField
                                fullWidth
                                label="Date of Birth"
                                type="date"
                                value={child.dateOfBirth || ''}
                                onChange={(e) => handleChildChange(index, 'dateOfBirth', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                required
                                placeholder=""
                              />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <TextField
                                fullWidth
                                select
                                label="Grade"
                                value={child.grade}
                                onChange={(e) => handleChildChange(index, 'grade', parseInt(e.target.value))}
                                required
                              >
                                {Array.from({ length: 12 }, (_, i) => (
                                  <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                                ))}
                              </TextField>
                            </Grid>

                            {/* Catholic School */}
                            <Grid item xs={12}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={child.catholicSchool.attends}
                                    onChange={(e) => handleChildChange(index, 'catholicSchool.attends', e.target.checked)}
                                  />
                                }
                                label="Attends Catholic School"
                              />
                              {child.catholicSchool.attends && (
                                <TextField
                                  fullWidth
                                  label="School Name"
                                  value={child.catholicSchool.schoolName}
                                  onChange={(e) => handleChildChange(index, 'catholicSchool.schoolName', e.target.value)}
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </Grid>

                            {/* Sacraments Received */}
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth>
                                <InputLabel>Sacraments Received</InputLabel>
                                <Select
                                  multiple
                                  value={child.sacraments.received}
                                  onChange={(e) => handleMultiSelectChange(index, 'received', e.target.value)}
                                  renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {selected.map((value) => (
                                        <Chip key={value} label={value.charAt(0).toUpperCase() + value.slice(1)} size="small" />
                                      ))}
                                    </Box>
                                  )}
                                >
                                  {sacramentOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>

                            {/* Sacraments Preparing */}
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth>
                                <InputLabel>Sacraments Preparing For</InputLabel>
                                <Select
                                  multiple
                                  value={child.sacraments.preparing}
                                  onChange={(e) => handleMultiSelectChange(index, 'preparing', e.target.value)}
                                  renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {selected.map((value) => (
                                        <Chip key={value} label={value.charAt(0).toUpperCase() + value.slice(1)} size="small" />
                                      ))}
                                    </Box>
                                  )}
                                >
                                  {sacramentOptions
                                    .filter(option => !child.sacraments.received.includes(option.value))
                                    .map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                </Select>
                              </FormControl>
                            </Grid>

                            {/* Baptism Questions */}
                            {child.sacraments.received.includes('baptism') && (
                              <>
                                <Grid item xs={12} sm={6}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={child.baptism.baptizedAtStBridget}
                                        onChange={(e) => handleChildChange(index, 'baptism.baptizedAtStBridget', e.target.checked)}
                                      />
                                    }
                                    label="Baptized at St. Bridget"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={child.baptism.previouslyEnrolled}
                                        onChange={(e) => handleChildChange(index, 'baptism.previouslyEnrolled', e.target.checked)}
                                      />
                                    }
                                    label="Previously enrolled in Religious Education"
                                  />
                                </Grid>
                              </>
                            )}

                            {/* Program Selection — only render when programs exist; auto-enroll when exactly one */}
                            {programs.length > 1 && (
                              <Grid item xs={12}>
                                <FormControl fullWidth>
                                  <InputLabel>Program Selection *</InputLabel>
                                  <Select
                                    multiple
                                    value={child.program}
                                    onChange={(e) => handleChildChange(index, 'program', e.target.value)}
                                    renderValue={(selected) => (
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                          <Chip
                                            key={value}
                                            label={programOptions.find(p => p.value === value)?.label || value}
                                            size="small"
                                          />
                                        ))}
                                      </Box>
                                    )}
                                    required
                                  >
                                    {programOptions.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                            )}

                            {programs.length === 1 && (
                              <Grid item xs={12}>
                                <Alert severity="info">
                                  Will be enrolled in: <strong>{programs[0].name}</strong>
                                </Alert>
                              </Grid>
                            )}

                            {programs.length === 0 && (
                              <Grid item xs={12}>
                                <Alert severity="warning">
                                  No active programs are configured. Add programs in Religious Education Settings before registering.
                                </Alert>
                              </Grid>
                            )}
                          </Grid>
                        </CardContent>
                      </Collapse>
                    </Card>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Permissions & Notifications */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ChurchIcon color="primary" />
                  Permissions & Notifications
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.permissions.photoPermission}
                        onChange={(e) => handleChange('permissions.photoPermission', e.target.checked)}
                      />
                    }
                    label="Permission to photograph child and family during faith formation activities and parish events"
                  />
                  <Divider />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.notifications.sendEmails}
                        onChange={(e) => handleChange('notifications.sendEmails', e.target.checked)}
                      />
                    }
                    label="Send confirmation email to family and admin notification"
                    sx={{ 
                      '& .MuiFormControlLabel-label': { 
                        fontWeight: 500,
                        color: formData.notifications.sendEmails ? 'success.main' : 'text.primary'
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
                    {formData.notifications.sendEmails 
                      ? "✅ Email confirmations will be sent to the family and parish staff"
                      : "📧 Email notifications are disabled - family will not receive confirmation"}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Payment Information */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaymentIcon color="primary" />
                  Payment Information
                </Typography>

                {/* Fee Breakdown */}
                {settings && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        📄 Registration Receipt
                        {settings.discount?.enabled && (
                          <Chip 
                            label={`Early Registration Discount - Ends ${new Date(settings.discount.endDate).toLocaleDateString()}`}
                            color="success" 
                            size="small" 
                            sx={{ ml: 2 }} 
                          />
                        )}
                      </Typography>
                      
                      {(() => {
                        const breakdown = calculateDetailedBreakdown();
                        
                        if (breakdown.items.length === 0) {
                          return (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              Add children and select programs to see fee breakdown
                            </Typography>
                          );
                        }

                        return (
                          <Box>
                            {/* Line Items */}
                            {breakdown.items.map((item, index) => (
                              <Box 
                                key={index}
                                sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  py: 1,
                                  borderBottom: index < breakdown.items.length - 1 ? '1px solid' : 'none',
                                  borderColor: 'divider'
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {item.description}
                                  </Typography>
                                  {item.isDiscount && (
                                    <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                                      Early Registration Discount Applied
                                    </Typography>
                                  )}
                                  {item.isFree && (
                                    <Typography variant="caption" color="info.main" sx={{ display: 'block' }}>
                                      Free for Catholic School Students
                                    </Typography>
                                  )}
                                </Box>
                                
                                <Box sx={{ textAlign: 'right', minWidth: 80 }}>
                                  {item.isDiscount && item.discountedPrice !== null ? (
                                    <Box>
                                      <Typography 
                                        variant="caption" 
                                        sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                                      >
                                        ${item.originalPrice.toFixed(2)}
                                      </Typography>
                                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                                        ${item.amount.toFixed(2)}
                                      </Typography>
                                    </Box>
                                  ) : item.isFree ? (
                                    <Box>
                                      <Typography 
                                        variant="caption" 
                                        sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                                      >
                                        ${item.originalPrice.toFixed(2)}
                                      </Typography>
                                      <Typography variant="body2" color="info.main" sx={{ fontWeight: 600 }}>
                                        FREE
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      ${item.amount.toFixed(2)}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            ))}
                            
                            {/* Total Line */}
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                pt: 2,
                                mt: 1,
                                borderTop: '2px solid',
                                borderColor: 'primary.main'
                              }}
                            >
                              <Typography variant="h6" color="primary">
                                Total Amount Due:
                              </Typography>
                              <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                                ${breakdown.total.toFixed(2)}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })()}
                    </Box>
                  </Box>
                )}

                {/* Payment Summary */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={9}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-around', p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Total Amount Due</Typography>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                          ${formData.payment.amount.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Total Paid</Typography>
                        <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                          ${formData.payment.totalPaid.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Balance Due</Typography>
                        <Typography 
                          variant="h6" 
                          color={formData.payment.balance > 0 ? "warning.main" : "success.main"}
                          sx={{ fontWeight: 600 }}
                        >
                          ${formData.payment.balance.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pl: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.payment.isHardship}
                            onChange={(e) => handleChange('payment.isHardship', e.target.checked)}
                          />
                        }
                        label="Hardship Assistance"
                      />
                    </Box>
                  </Grid>
                </Grid>

                {/* Add New Payment */}
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>Add Payment</Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Payment Amount"
                        type="number"
                        value={newPayment.amount}
                        onChange={(e) => setNewPayment(prev => ({ ...prev, amount: e.target.value }))}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        select
                        label="Payment Method"
                        value={newPayment.method}
                        onChange={(e) => setNewPayment(prev => ({ ...prev, method: e.target.value }))}
                      >
                        <MenuItem value="cash">Cash</MenuItem>
                        <MenuItem value="check">Check</MenuItem>
                        <MenuItem value="online">Online</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Notes (optional)"
                        value={newPayment.notes}
                        onChange={(e) => setNewPayment(prev => ({ ...prev, notes: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Button
                        variant="contained"
                        onClick={addPayment}
                        fullWidth
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>
                </Box>

                {/* Payment History */}
                {formData.payment.transactions.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>Payment History</Typography>
                    {formData.payment.transactions.map((transaction, index) => (
                      <Box 
                        key={index}
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1
                        }}
                      >
                        <Box>
                          <Typography variant="body2">
                            <strong>${transaction.amount.toFixed(2)}</strong> via {transaction.method}
                          </Typography>
                          {transaction.notes && (
                            <Typography variant="caption" color="text.secondary">
                              {transaction.notes}
                            </Typography>
                          )}
                        </Box>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => removePayment(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Signature */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Signature</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Signature Name"
                      value={formData.signature.name}
                      onChange={(e) => handleChange('signature.name', e.target.value)}
                      required
                      helperText="Enter name of person who signed (parent, guardian, or whoever completed registration)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Date"
                      type="date"
                      value={formData.signature.date}
                      onChange={(e) => handleChange('signature.date', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Validation Status */}
        {!isFormValid() && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Please complete the following required fields:
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              {getValidationStatus().map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </Box>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} size="large">Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!isFormValid()}
          size="large"
        >
          {isFormValid()
            ? 'Create Manual Registration'
            : `Complete Required Fields (${getValidationStatus().length} missing)`
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReligiousEducationForm;